import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';

const WorkspaceContext = createContext(null);

const SELECTED_WORKSPACE_KEY = 'selectedWorkspaceId';

export function WorkspaceProvider({ children }) {
  const { token } = useAuth();
  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(
    () => localStorage.getItem(SELECTED_WORKSPACE_KEY) || null
  );
  const [loading, setLoading] = useState(true);
  const [loadedToken, setLoadedToken] = useState(null); // token the workspace list was last fetched for
  const [proDialogOpen, setProDialogOpen] = useState(false);

  const fetchWorkspaces = useCallback(async () => {
    if (!token) {
      setWorkspaces([]);
      setLoading(false);
      return;
    }
    try {
      const res = await api.get('/workspaces');
      const list = res.data.workspaces || [];
      setWorkspaces(list);

      // If selected workspace no longer exists, clear it
      if (selectedWorkspaceId && !list.find(w => w.id === selectedWorkspaceId)) {
        const firstId = list[0]?.id || null;
        setSelectedWorkspaceId(firstId);
        if (firstId) {
          localStorage.setItem(SELECTED_WORKSPACE_KEY, firstId);
          api.defaults.headers.common['X-Workspace-ID'] = firstId;
        } else {
          localStorage.removeItem(SELECTED_WORKSPACE_KEY);
          delete api.defaults.headers.common['X-Workspace-ID'];
        }
      } else if (!selectedWorkspaceId && list.length > 0) {
        // Auto-select first workspace
        const firstId = list[0].id;
        setSelectedWorkspaceId(firstId);
        localStorage.setItem(SELECTED_WORKSPACE_KEY, firstId);
        api.defaults.headers.common['X-Workspace-ID'] = firstId;
      } else if (selectedWorkspaceId) {
        // Restore header
        api.defaults.headers.common['X-Workspace-ID'] = selectedWorkspaceId;
      }
    } catch (err) {
      console.error('Failed to fetch workspaces:', err);
    } finally {
      setLoadedToken(token);
      setLoading(false);
    }
  }, [token, selectedWorkspaceId]);

  useEffect(() => {
    fetchWorkspaces();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const selectWorkspace = useCallback((workspaceId) => {
    setSelectedWorkspaceId(workspaceId);
    if (workspaceId) {
      localStorage.setItem(SELECTED_WORKSPACE_KEY, workspaceId);
      api.defaults.headers.common['X-Workspace-ID'] = workspaceId;
    } else {
      localStorage.removeItem(SELECTED_WORKSPACE_KEY);
      delete api.defaults.headers.common['X-Workspace-ID'];
    }
  }, []);

  const selectedWorkspace = workspaces.find(w => w.id === selectedWorkspaceId) || null;
  const myWorkspaceRole = selectedWorkspace?.myRole || null;
  const isWorkspaceAdmin = myWorkspaceRole === 'admin';

  // Plan / limits (served by the backend on every workspace)
  const plan = selectedWorkspace?.plan || null;
  const planLabel = plan === 'pro' ? 'Pro' : 'Free';
  const limits = selectedWorkspace?.limits || { maxChannels: 2 };
  const usage = selectedWorkspace?.usage || { channels: 0 };
  const isAtChannelLimit = limits.maxChannels != null && usage.channels >= limits.maxChannels;
  const openProDialog = useCallback(() => setProDialogOpen(true), []);
  const closeProDialog = useCallback(() => setProDialogOpen(false), []);

  const value = {
    workspaces,
    selectedWorkspace,
    selectedWorkspaceId,
    selectWorkspace,
    myWorkspaceRole,
    isWorkspaceAdmin,
    plan,
    planLabel,
    limits,
    usage,
    isAtChannelLimit,
    proDialogOpen,
    openProDialog,
    closeProDialog,
    // still loading until the list has been fetched for the current token
    loading: loading || (!!token && loadedToken !== token),
    refreshWorkspaces: fetchWorkspaces,
  };

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error('useWorkspace must be used within WorkspaceProvider');
  return ctx;
}

export default WorkspaceContext;
